import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { DateTime } from "luxon"

// Context
import { UserContext } from "../../../context/userContext"

//styling
import assessStyles from "../../../styles/assessments/Assessment.module.css"
import myStyles from "../../../styles/assessments/ParticipantAssessments.module.css"

//PrimeReact
import { Button } from "primereact/button"
import { Paginator } from "primereact/paginator"

//Participant components
import { assessmentsApiScheduleListTasksPost } from "../assessmentsApi"
import CustomTable from "../../../components/CustomTable/CustomTable"

//Helpers
import { LogError } from "../../../helpers/logger"
import { LoaderMedium } from "../../../components/Loaders"
import { useLazyQuery } from "@apollo/client"
import { GET_USERS_FROM_LIST } from "../queries"
import isParticipantActive from "../../../helpers/isParticipantActive"
import { formatToLocalDate } from "../../../helpers/dateConversion"
import {
  FilterDefaults,
  FilterLocation,
  FilterTypes,
} from "../../../components/CustomFilter/FilterConstants"
import CustomFilter from "../../../components/CustomFilter/CustomFilter"
import { formatCompletedDateToLocaleString } from "../assessmentHelpers"

export default function ParticipantAssessmentsSchedules({
  triggerRefresh,
  onProcessScheduledTask,
  selectedOrganizationId,
  setIsChildLoading,
}) {
  const userCtx = useContext(UserContext)
  const [isLoading, setIsLoading] = useState(true)
  const [dataset, setDataset] = useState(undefined)
  const [staffNames, setStaffNames] = useState([])

  // Filters & sorting
  const defaultFilters = [
    {
      key: "search_filter",
      label: "Search",
      placeholder: "Keyword Search",
      defaultValue: FilterDefaults.text,
      currentValue: FilterDefaults.text,
      type: FilterTypes.text,
      location: FilterLocation.bar,
    },
    {
      key: "due_date_filter",
      label: "Due Date",
      placeholder: "Due Date (Range)",
      defaultValue: FilterDefaults.calendar,
      currentValue: FilterDefaults.calendar,
      type: FilterTypes.calendar,
      location: FilterLocation.bar,
      showTime: false,
      showIcon: true,
      rangeSelect: true,
    },
    {
      key: "include_upcoming_filter",
      label: "Include Upcoming",
      defaultValue: true,
      currentValue: true,
      type: "checkbox",
      location: FilterLocation.bar,
      placeholder: "Check Test",
    },
    {
      key: "include_completed_filter",
      label: "Include Completed",
      defaultValue: false,
      currentValue: false,
      type: "checkbox",
      location: FilterLocation.bar,
      placeholder: "Check Test",
    },
  ]

  const defaultSorting = [
    { name: "Name", code: "name", direction: "ASC", enabled: false },
    {
      name: "Assigned by",
      code: "assignedBy",
      direction: "ASC",
      enabled: false,
    },
    { name: "Due date", code: "dueDate", direction: "ASC", enabled: true },
    {
      name: "Completed date",
      code: "completedDate",
      direction: "ASC",
      enabled: false,
    },
  ]
  const [assessmentFilters, setAssessmentFilters] = useState(defaultFilters)
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters)
  const [assessmentSorting, setAssessmentSorting] = useState(defaultSorting)
  const [appliedSorting, setAppliedSorting] = useState(defaultSorting)

  // Pagination
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(0)

  // Handle screen resize in realtime
  const screenType = useRef("")
  const screenTypeLoading = useRef(false)

  useEffect(() => {
    const handleResize = () => {
      if (!screenTypeLoading.current) {
        if (window.innerWidth <= 768 && screenType.current !== "mobile") {
          screenTypeLoading.current = true
          screenType.current = "mobile"

          const _filters = JSON.parse(JSON.stringify(assessmentFilters))
          _filters.forEach((f) => {
            f.location = FilterLocation.panel
          })
          setAssessmentFilters(_filters)

          screenTypeLoading.current = false
        } else if (window.innerWidth > 768 && screenType.current !== "normal") {
          screenTypeLoading.current = true
          screenType.current = "normal"

          const _filters = JSON.parse(JSON.stringify(assessmentFilters))
          _filters.forEach((f) => {
            f.location = FilterLocation.bar
          })
          setAssessmentFilters(_filters)

          screenTypeLoading.current = false
        }
      }
    }
    handleResize()

    window.addEventListener("resize", handleResize)
    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("resize", handleResize)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true)
      setIsChildLoading(true)

      // Get filter items
      const searchFilter = getFilterByKey("search_filter")
      const dueDateFilter = getFilterByKey("due_date_filter")
      const includeUpcoming = getFilterByKey("include_upcoming_filter")
      const includeCompleted = getFilterByKey("include_completed_filter")

      // Modify due-date filter dates
      let dueDateFilterFrom, dueDateFilterTo
      if (
        dueDateFilter?.currentValue &&
        dueDateFilter?.currentValue.length === 2
      ) {
        dueDateFilter.currentValue[0] = DateTime.fromJSDate(
          dueDateFilter.currentValue[0],
        ).startOf("day")
        dueDateFilter.currentValue[1] = DateTime.fromJSDate(
          dueDateFilter.currentValue[1],
        ).endOf("day")
      }

      // Get enabled sorting
      const enabledSortingItem = appliedSorting.find((s) => s.enabled === true)
      const sortField = enabledSortingItem?.code || ""
      const sortOrder = enabledSortingItem?.direction
        ? enabledSortingItem?.direction === "DESC"
          ? "z-a"
          : "a-z"
        : ""

      const useFilters = {
        // ...filters,
        organizationID: selectedOrganizationId,
        participantId: userCtx.participant_id,
        searchPhrase: searchFilter?.currentValue || "",
        from: dueDateFilterFrom || null,
        to: dueDateFilterTo || null,
        includeUpcoming: includeUpcoming?.currentValue,
        includeCompleted: includeCompleted?.currentValue,
        sortField: sortField,
        sortOrder: sortOrder,
        pageSize: pageSize,
        pageNum: currentPage + 1,
      }

      const results = await assessmentsApiScheduleListTasksPost(
        userCtx.tokens.getAssessmentsToken(),
        useFilters,
      )

      if (results) {
        //Load staff details if there are assignments allocates to them
        let newStaffNames = staffNames

        let staff_ids = [
          ...new Set(results?.tasks?.map((task) => task.assignerId)),
        ]

        staff_ids = staff_ids.filter(
          (id) => !newStaffNames.find((staffName) => staffName.staff_id === id),
        )

        const staffResults = await loadStaffProfiles({
          variables: { list: staff_ids },
        })

        if (staffResults.data?.usersFromList) {
          newStaffNames = [
            ...newStaffNames,
            ...staffResults.data?.usersFromList,
          ]
          //cache the names
          setStaffNames(newStaffNames)
        } else {
          LogError("Unable to load users from list", staffResults)
        }

        results.tasks = results.tasks.map((task) => {
          let assignerName = "Unknown"

          const creator = newStaffNames?.find(
            (staff) => staff.staff_id === task.assignerId,
          )

          if (creator) {
            assignerName = creator.staff_full_name
          }

          return { ...task, assignerName }
        })

        setDataset(results)
      } else {
        setDataset([])
      }
    } catch (error) {
      LogError("Unable to list assignments", error)
      userCtx.setNotificationError("Unable to list assignments")

      setDataset(undefined)
    } finally {
      setIsLoading(false)
      setIsChildLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    triggerRefresh,
    appliedFilters,
    appliedSorting,
    currentPage,
    pageSize,
    userCtx.participantId,
    selectedOrganizationId,
  ])

  useEffect(() => {
    loadData()
  }, [loadData])

  function getFilterByKey(key) {
    const filter = appliedFilters.find((f) => f.key === key)
    return filter
  }

  const participantIsActive = (organization_id) =>
    isParticipantActive(
      userCtx.participant_organizations?.find(
        (o) => o.organization_id === organization_id,
      )?.participant_membership_status,
    )

  const [loadStaffProfiles] = useLazyQuery(GET_USERS_FROM_LIST, {
    fetchPolicy: "cache-and-network",
    skip: true,
    variables: {
      list: [],
    },
  })

  const RenderActionBody = (rowData) => {
    const dueDate = DateTime.fromISO(rowData.date)
    const today = DateTime.now()

    let doDisable = false
    let tooltip = ""

    if (rowData.dateCompleted) {
      tooltip = "View assessments"
    } else if (!participantIsActive(selectedOrganizationId)) {
      return <></>
    } else if (dueDate.startOf("day").ts <= today.startOf("day").ts) {
      tooltip = "Complete assessments"
      doDisable = !participantIsActive(selectedOrganizationId)
    } else {
      tooltip =
        "This assessment will be available to complete on the due date. Please try again at that time."
      doDisable = true
    }

    return (
      <div className={assessStyles.actionButtonContainer}>
        <Button
          type="button"
          onClick={() => {
            onProcessScheduledTask(rowData.id)
          }}
          className={assessStyles.actionButtonPurple}
          tooltip={tooltip}
          tooltipOptions={{ position: "bottom", showOnDisabled: true }}
          disabled={doDisable}
        >
          {rowData.dateCompleted ? "View Assessments" : "Complete Assessments"}
        </Button>
      </div>
    )
  }

  const headers = () => {
    return [
      {
        value: "name",
        label: "Name",
        size: 3,
      },
      {
        value: "assignerName",
        label: "Asigned By",
        size: 2,
      },
      {
        value: "date",
        label: "Due Date",
        size: 2,
      },
      {
        value: "dateCompleted",
        label: "Date Completed",
        action: false,
        size: 2,
      },

      {
        value: "view",
        label: "",
        action: true,
        size: 3,
      },
    ]
  }

  const mapData = () => {
    if (!dataset?.tasks.length) {
      return []
    }

    return dataset?.tasks.map((d) => {
      return {
        name: d.name,
        assignerName: d.assignerName || "Unknown",
        date: d.date ? formatToLocalDate(d.date) : "-",
        dateCompleted: d.dateCompleted
          ? formatCompletedDateToLocaleString(d.dateCompleted)
          : "-",
        view: <RenderActionBody {...d} />,
      }
    })
  }

  return (
    <>
      <div className={myStyles.completedLayout}>
        <div className={myStyles.completedLayoutBody}>
          {isLoading ? (
            <LoaderMedium />
          ) : (
            <>
              <div className={myStyles.headerCompletedAssessments}>
                <div className={myStyles.completedLayoutFilters}>
                  <CustomFilter
                    filters={assessmentFilters}
                    setFilters={setAssessmentFilters}
                    sorting={assessmentSorting}
                    singleSortOnly
                    setSorting={setAssessmentSorting}
                    applyFilters={() => {
                      setCurrentPage(0)
                      setAppliedFilters(assessmentFilters)
                      setAppliedSorting(assessmentSorting)
                    }}
                  />
                </div>
                <div className={myStyles.refreshContainer}>
                  <Button
                    icon="pi pi-sync"
                    className={`${assessStyles.button} ${myStyles.refreshButton}`}
                    onClick={() => {
                      loadData()
                    }}
                    tooltip={`${"Refresh data"}`}
                    tooltipOptions={{ position: "bottom" }}
                  />
                </div>
              </div>
              <CustomTable
                headers={headers()}
                collection={mapData()}
                styles={{
                  padding: "16px 0px",
                  borderRadius: "12px",
                  marginTop: "0px",
                }}
                tableRowStyles={{ backgroundColor: "#FFFFFF" }}
                disablePagination
              />
              <Paginator
                first={dataset ? (dataset?.pageNum - 1) * dataset?.pageSize : 0}
                rows={dataset?.pageSize || 0}
                totalRecords={dataset?.numTotal || 0}
                style={{ marginBottom: "40px" }} // add som spacing at the bottom of the page
                onPageChange={(setup) => {
                  setPageSize(setup.rows)
                  setCurrentPage(setup.page)
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </div>
      </div>
    </>
  )
}
